import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Subject, Observable } from 'rxjs';
import { Sidebar, ChatDetails, HideComponent, RefreshHistory, HideRoleplay, LayoutState } from '../../model/interaction.model';
import { HttpClient } from '@angular/common/http';
import { CONVERSATION_SERVICE, ADMIN_SERVICE } from "../../service/service-url";
import { tap } from 'rxjs/operators';
import { DEFAULT_PLATFORM_LABEL } from '../../utility/constant-config';

@Injectable({
  providedIn: 'root'
})
export class InteractionService {

  readonly layoutState = signal<LayoutState>({
    headerVisible: true,
    sidebarVisible: true,
    fullHeaderVisible: false,
    rolePlayActive: false,
    sidebarCollapsed: false
  });

  public refreshMenuList = new Subject<void>();
  
  private readonly refreshHeader = new BehaviorSubject<string>('');
  refreshHeader$ = this.refreshHeader.asObservable();

  private readonly sidebar = new BehaviorSubject({showSidebar: true});
  currentData = this.sidebar.asObservable();

  // Single source of truth for the left sidebar collapse (icon-rail) state.
  // false = expanded (full sidebar), true = collapsed (icons only).
  private readonly sidebarCollapsed = new BehaviorSubject<boolean>(false);
  sidebarCollapsed$ = this.sidebarCollapsed.asObservable();

  private  readonly newchat = new Subject();
  chatstatus = this.newchat.asObservable();

  public recognizespeech = new Subject();
  recognize$ = this.recognizespeech.asObservable();

  stopSessionSubject = new Subject();
  stopSession$ = this.stopSessionSubject.asObservable();
  activeMenu = new Subject();
  private readonly hideComponentSubject = new Subject();
  private readonly hideRoleplaySubject = new Subject();
  hideComponentObservable$ = this.hideComponentSubject.asObservable();
  hideRoleplayObservable$ = this.hideRoleplaySubject.asObservable();

  private readonly systemPromptSubject = new BehaviorSubject<string>('');
  systemPromptObservable$ = this.systemPromptSubject.asObservable();

  private readonly reloadHistorySubject = new BehaviorSubject<RefreshHistory>({refreshMenu: 'Chat', subType: 'Chat'});
  reloadHistoryObservable$ = this.reloadHistorySubject.asObservable();

  private readonly dataSubject$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  public data$: Observable<any[]> = this.dataSubject$.asObservable();

  private readonly productSubject$ = new BehaviorSubject<any[]>([]);
  public isProductSelected = new Subject();
  public productObservable$: Observable<any[]> = this.productSubject$.asObservable();

  private readonly loadHistoryDetailsSubject =new Subject<string>();  // chat: FAQ|ROLEPLAY
  // private readonly loadHistoryDetailsSubject =new Subject<string>();  // chat: FAQ|ROLEPLAY
  loadHistorydetailsObservable$ = this.loadHistoryDetailsSubject.asObservable();

  private readonly chatSession = new BehaviorSubject<string>('');
  chatSessionObservable$ = this.chatSession.asObservable();

  private customizationDetailsSubject = new BehaviorSubject<any>(null);  // Default value as null or empty object
  public customizationDetails$ = this.customizationDetailsSubject.asObservable();

  public labelSubject = new BehaviorSubject<string>(DEFAULT_PLATFORM_LABEL);
  
  private lastEmittedValue: any = null;

  
  private refreshSubject = new Subject<void>();

  private regenerateSubject = new Subject<{  additionalInfo: string }>();
  regenerate$ = this.regenerateSubject.asObservable();

  get refresh$() {
    return this.refreshSubject.asObservable();
  }

  constructor(private readonly http: HttpClient) { }

  triggerRefreshHeader(value: string) {
    this.refreshHeader.next(value);
  }
  changeData(value: Sidebar) {
    this.sidebar.next(value);
  }

  // Flip the sidebar between expanded and collapsed (icon-rail) states.
  toggleSidebar() {
    this.setSidebarCollapsed(!this.layoutState().sidebarCollapsed);
  }

  // Explicitly set the collapse state (e.g. auto-collapse on small screens).
  setSidebarCollapsed(collapsed: boolean) {
    if (this.layoutState().sidebarCollapsed !== collapsed) {
      this.layoutState.update(state => ({ ...state, sidebarCollapsed: collapsed }));
      this.sidebarCollapsed.next(collapsed);
    }
  }
  updateValue(newValue: string) {
    this.chatSession.next(newValue);
  }
  setProduct(value: any) {
    this.productSubject$.next(value);
  }

  endRoleplayUpdate(value: string) {
    this.stopSessionSubject.next(value);
  }
  setChatStatus(value: ChatDetails) {
    this.newchat.next(value);
  }
  triggerSpeechRecognition() {
    this.recognizespeech.next(new Date());
  }

  hideComponentHandler(value: HideComponent) {
    this.layoutState.update(state => ({
      ...state,
      ...(value.hideHeader === undefined ? {} : { headerVisible: !value.hideHeader }),
      ...(value.hideSidebar === undefined ? {} : { sidebarVisible: !value.hideSidebar }),
      ...(value.hideFullHeader === undefined ? {} : { fullHeaderVisible: !value.hideFullHeader })
    }));
    this.hideComponentSubject.next(value);
  }

   hideRolelplayHandler(value: HideRoleplay) {
    if (value.rolePlayActive !== undefined) {
      this.layoutState.update(state => ({ ...state, rolePlayActive: value.rolePlayActive as boolean }));
    }
    this.hideRoleplaySubject.next(value);
  }

  

  refreshHistoryHandler(value: RefreshHistory) {
    this.reloadHistorySubject.next(value);
  }

  setSystemPrompt(value: any) {
    this.systemPromptSubject.next(value);
  }
  
  loadHistoryDetailsHandler(value: any) {
    this.lastEmittedValue = value;
    this.loadHistoryDetailsSubject.next(value);
  }

  getLastEmittedValue() {
    return this.lastEmittedValue;
  }

  getloadHistoryDetailsHandler () {
    return this.loadHistoryDetailsSubject.asObservable();
  }

  updateSystemPromptType(chat_type: string): void {
    this.systemPromptSubject.next(chat_type);
  }

  getSystemPrompt(chat_type: string): Observable<any> {
    return this.http.get(`${ADMIN_SERVICE.systemPrompt}/${chat_type}`);
  }
  updateCustomizationDetails(value: any): void {
    this.customizationDetailsSubject.next(value);
  }

  fetchData(): Observable<any[]> {
    return this.http.get<any[]>(CONVERSATION_SERVICE.getConversationFeedbackMetadataList).pipe(
      tap(data => this.dataSubject$.next(data))
    );
  }
  decodeUrl(encodedUrl: string): string {
    try {
      return decodeURIComponent(encodedUrl);
    } catch (e) {
      return encodedUrl;
    }
  }

  triggerRefresh() {
    this.refreshSubject.next();
  }
    triggerRegenerate( additionalInfo: any) {
    this.regenerateSubject.next({  additionalInfo });
  }

}
